import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tableHeadCellSx, tokens } from '../../theme/tokens';

export default function ReportsEventsData() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/reports/events-data');
      setData(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch events data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Events data" />
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress sx={{ color: tokens.primary }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>} title="Events data" />
        <Alert severity="error" sx={{ borderRadius: '9px', bgcolor: tokens.dangerSoft, color: '#7C2E1D', border: '1px solid #EFCFC5' }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={<>Insight / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Reports</Box></>}
        title="Events data"
      />

      <Paper elevation={0} sx={{ ...surfaceCardSx, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>Event</TableCell>
                <TableCell sx={tableHeadCellSx}>Date</TableCell>
                <TableCell sx={tableHeadCellSx}>Location</TableCell>
                <TableCell sx={tableHeadCellSx}>Attendees</TableCell>
                <TableCell sx={tableHeadCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box sx={{ py: 5.5, color: tokens.muted }}>
                      <Typography sx={{ fontWeight: 600, color: tokens.ink, mb: 0.5 }}>No data available</Typography>
                      <Typography variant="body2">Event reports will appear here when available.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => (
                  <TableRow key={index} hover sx={{ '&:hover': { bgcolor: '#F4F7F5' }, '& td': { borderColor: tokens.border } }}>
                    <TableCell>{row.name || row.event_name}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.date || '—'}</TableCell>
                    <TableCell>{row.location || '—'}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.attendees ?? row.attendee_count ?? '—'}</TableCell>
                    <TableCell>{row.status || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
